/**
 * Quantum Circuit Engine – State Vector Simulation
 *
 * Simulates up to 3 qubits using exact complex-number state vector math.
 * Convention: qubit 0 = top wire = most-significant bit in state index.
 *   index 0 = |000⟩, index 1 = |001⟩ (qubit 2 = 1), etc.
 */

// ─── Complex number helpers ──────────────────────────────────────────────────
export type Complex = [number, number]; // [real, imag]

const cadd = (a: Complex, b: Complex): Complex => [a[0] + b[0], a[1] + b[1]];
const cmul = (a: Complex, b: Complex): Complex => [
  a[0] * b[0] - a[1] * b[1],
  a[0] * b[1] + a[1] * b[0],
];
const cmod2 = (a: Complex): number => a[0] * a[0] + a[1] * a[1];
const cscale = (a: Complex, s: number): Complex => [a[0] * s, a[1] * s];

// ─── Types ───────────────────────────────────────────────────────────────────

export type SingleQubitGate = 'H' | 'X' | 'Y' | 'Z' | 'S' | 'T';
export type GateType = SingleQubitGate | 'CNOT' | 'MEASURE' | 'CNOT_TARGET';

export interface SingleGateCell {
  gate: SingleQubitGate | 'MEASURE';
}

export interface CnotControlCell {
  gate: 'CNOT';
  targetQubit: number;
}

export interface CnotTargetCell {
  gate: 'CNOT_TARGET';
  controlQubit: number;
}

export type CircuitCell = SingleGateCell | CnotControlCell | CnotTargetCell;

export type StateVector = Complex[]; // length = 2^numQubits

export interface StepState {
  state: StateVector;
  label: string; // e.g. "After H on q0"
}

export interface CircuitResult {
  finalState: StateVector;
  probabilities: number[];
  basisLabels: string[];
  stepStates: StepState[];
  measureResults: Record<number, 0 | 1>; // qubit → measured value
}

// ─── Gate matrices: [m00, m01, m10, m11] ─────────────────────────────────────
// new_|0⟩ = m00 * old_|0⟩ + m01 * old_|1⟩
// new_|1⟩ = m10 * old_|0⟩ + m11 * old_|1⟩
type GateMat = [Complex, Complex, Complex, Complex];

const INV2 = 1 / Math.sqrt(2);
const PI4 = Math.PI / 4;

const GATE_MATS: Record<SingleQubitGate, GateMat> = {
  H: [[INV2, 0], [INV2, 0], [INV2, 0], [-INV2, 0]],
  X: [[0, 0], [1, 0], [1, 0], [0, 0]],
  Y: [[0, 0], [0, -1], [0, 1], [0, 0]],
  Z: [[1, 0], [0, 0], [0, 0], [-1, 0]],
  S: [[1, 0], [0, 0], [0, 0], [0, 1]],
  T: [[1, 0], [0, 0], [0, 0], [Math.cos(PI4), Math.sin(PI4)]],
};

// ─── Core gate operations ─────────────────────────────────────────────────────

/** Stride for qubit k in an n-qubit system (qubit 0 = MSB) */
const stride = (n: number, qubit: number) => 1 << (n - 1 - qubit);

/** Apply a single-qubit gate to qubit `q` in an n-qubit state */
function applySingle(
  state: StateVector,
  n: number,
  qubit: number,
  gate: SingleQubitGate
): StateVector {
  const s = stride(n, qubit);
  const size = 1 << n;
  const [m00, m01, m10, m11] = GATE_MATS[gate];
  const next = state.map((c) => [c[0], c[1]] as Complex);

  for (let i = 0; i < size; i++) {
    if ((i & s) === 0) {
      const j = i | s;
      const a = state[i];
      const b = state[j];
      next[i] = cadd(cmul(m00, a), cmul(m01, b));
      next[j] = cadd(cmul(m10, a), cmul(m11, b));
    }
  }
  return next;
}

/** Apply CNOT with control qubit c and target qubit t */
function applyCNOT(
  state: StateVector,
  n: number,
  control: number,
  target: number
): StateVector {
  const cs = stride(n, control);
  const ts = stride(n, target);
  const size = 1 << n;
  const next = state.map((c) => [c[0], c[1]] as Complex);

  for (let i = 0; i < size; i++) {
    // Only process when control=1 and target=0 to avoid double-swapping
    if ((i & cs) !== 0 && (i & ts) === 0) {
      const j = i | ts;
      next[i] = state[j];
      next[j] = state[i];
    }
  }
  return next;
}

/** Measure qubit q: collapse state, return new state + measured value */
function measureQubit(
  state: StateVector,
  n: number,
  qubit: number
): { state: StateVector; value: 0 | 1 } {
  const s = stride(n, qubit);
  const size = 1 << n;
  let p0 = 0;
  for (let i = 0; i < size; i++) {
    if ((i & s) === 0) p0 += cmod2(state[i]);
  }

  const value: 0 | 1 = Math.random() < p0 ? 0 : 1;
  const norm = Math.sqrt(value === 0 ? p0 : 1 - p0);
  const next = state.map((_, i) => {
    const bit = (i & s) === 0 ? 0 : 1;
    return bit === value ? cscale(state[i], norm > 0 ? 1 / norm : 0) : ([0, 0] as Complex);
  });

  return { state: next, value };
}

// ─── Basis label helpers ──────────────────────────────────────────────────────

export function basisLabels(n: number): string[] {
  const labels: string[] = [];
  for (let i = 0; i < 1 << n; i++) {
    let bits = '';
    for (let q = 0; q < n; q++) {
      bits += (i >> (n - 1 - q)) & 1;
    }
    labels.push(`|${bits}⟩`);
  }
  return labels;
}

// ─── High-level runner ────────────────────────────────────────────────────────

export interface CircuitRunConfig {
  numQubits: 1 | 2 | 3;
  /** Initial state per qubit: '0' or '1' */
  initialStates: ('0' | '1')[];
  /** grid[step][qubit] – null means identity */
  grid: (CircuitCell | null)[][];
  /** How many columns to simulate (for step-through) */
  upToStep?: number;
}

export function runCircuit(config: CircuitRunConfig): CircuitResult {
  const { numQubits: n, initialStates, grid } = config;
  const size = 1 << n;
  const upTo = config.upToStep ?? grid.length;

  // Build initial state vector
  let state: StateVector = Array.from({ length: size }, () => [0, 0] as Complex);
  let initIdx = 0;
  for (let q = 0; q < n; q++) {
    if (initialStates[q] === '1') initIdx |= stride(n, q);
  }
  state[initIdx] = [1, 0];

  const stepStates: StepState[] = [
    { state: state.map((c) => [c[0], c[1]] as Complex), label: 'Initial state' },
  ];
  const measureResults: Record<number, 0 | 1> = {};

  for (let step = 0; step < Math.min(upTo, grid.length); step++) {
    const column = grid[step];
    if (!column) continue;

    // Single-qubit gates first, then CNOT
    for (let q = 0; q < n; q++) {
      const cell = column[q];
      if (!cell) continue;

      if (cell.gate === 'CNOT') {
        state = applyCNOT(state, n, q, (cell as CnotControlCell).targetQubit);
      } else if (cell.gate === 'CNOT_TARGET') {
        // handled by control cell
      } else if (cell.gate === 'MEASURE') {
        const { state: next, value } = measureQubit(state, n, q);
        state = next;
        measureResults[q] = value;
      } else {
        state = applySingle(state, n, q, cell.gate as SingleQubitGate);
      }
    }

    const gateNames = column
      .map((c, q) => {
        if (!c) return null;
        if (c.gate === 'CNOT') return `CNOT(q${q}→q${(c as CnotControlCell).targetQubit})`;
        if (c.gate === 'CNOT_TARGET') return null;
        return `${c.gate}(q${q})`;
      })
      .filter(Boolean)
      .join(', ');

    stepStates.push({
      state: state.map((c) => [c[0], c[1]] as Complex),
      label: gateNames || `Step ${step + 1}`,
    });
  }

  const probabilities = state.map(cmod2);
  const labels = basisLabels(n);

  return {
    finalState: state,
    probabilities,
    basisLabels: labels,
    stepStates,
    measureResults,
  };
}
